import { Download, ShieldAlert } from 'lucide-react';
import type { StagedEntry, StagedLink } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { defang } from '@/lib/preview/render';

/** What the document downloaded, and whether to run it too.
 *
 *  This is a question, not a result. The document itself detonates either way -
 *  Acrobat opens a PDF, Word opens a .docm - so the run the analyst came for is
 *  already selected. What is asked here is the second one: whether to also
 *  detonate the file the document reached out for.
 *
 *  It is asked rather than assumed because retrieving the payload has already
 *  told the actor their lure landed, and running it is a further step: a
 *  second detonation, on this deployment's machines, of bytes an attacker
 *  served on demand. Some of those exist to detect exactly this.
 *
 *  Nothing here is an anchor and every target is defanged, for the reason the
 *  reference list is. */

interface Props {
  /** The whole tree; the offers are the entries the server marked. */
  entries: StagedEntry[];
  /** The references, so each payload can say which one produced it. */
  links: StagedLink[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  disabled?: boolean;
}

export default function RetrievedPayloads({
  entries,
  links,
  selected,
  onToggle,
  disabled,
}: Props) {
  const offers = entries.filter((e) => e.offered);
  if (offers.length === 0) return null;

  /** The reference a payload came from, so the row says what reached out. */
  const sourceOf = (entryId: string): StagedLink | undefined =>
    links.find((l) => l.fetched_entry_id === entryId);

  return (
    <section
      className="rounded-lg border border-verdict-suspicious/40 bg-ink-900"
      aria-label="Files retrieved from this document"
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-ink-800 px-4 py-2.5">
        <Download className="h-4 w-4 text-verdict-suspicious" aria-hidden />
        <h3 className="text-prose font-medium text-ink-100">
          This document downloaded {offers.length === 1 ? 'a file' : `${offers.length} files`}
        </h3>
      </header>

      <p className="px-4 pt-3 text-prose text-ink-300">
        The document you submitted is queued to run on its own. Tick a file below to run it as a
        separate analysis as well.
      </p>

      <ul className="mt-2 divide-y divide-ink-800">
        {offers.map((entry) => {
          const source = sourceOf(entry.id);
          const inputId = `retrieved-${entry.id}`;
          return (
            <li key={entry.id} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <input
                  id={inputId}
                  type="checkbox"
                  checked={selected.has(entry.id)}
                  onChange={() => onToggle(entry.id)}
                  disabled={disabled}
                  className="mt-1 h-4 w-4 shrink-0 accent-accent-400"
                />
                <div className="min-w-0 flex-1">
                  <label
                    htmlFor={inputId}
                    className="block wrap-anywhere font-mono text-mini text-ink-100"
                  >
                    {entry.name}
                  </label>
                  <p className="mt-1 text-mini text-ink-400">
                    {entry.detected_format || 'unrecognised format'}
                    {entry.pe_kind ? ` · ${entry.pe_kind}` : ''} · {formatBytes(entry.size)}
                  </p>
                  {source?.url && (
                    // Deliberately a <span>, never an <a>, and defanged.
                    <p className="mt-1 break-all font-mono text-mini text-ink-500">
                      from {defang(source.final_url || source.url)}
                    </p>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <footer className="flex items-start gap-2 border-t border-ink-800 px-4 py-2 text-mini text-ink-500">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Retrieved by a provider host, never by the control plane. Running one detonates bytes an
          attacker served on demand, so it happens only if you ask for it.
        </span>
      </footer>
    </section>
  );
}
